/**
 * Post Editor - Create and edit social media posts
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Textarea,
  Button,
  VStack,
  HStack,
  useToast,
  Spinner,
  Text,
  Checkbox,
  CheckboxGroup,
  Stack,
  Input,
  Badge,
  Flex,
  Spacer,
  Alert,
  AlertIcon,
  Select,
  FormHelperText,
  Progress,
  Heading,
} from '@chakra-ui/react';
import {
  createPost,
  getPostById,
  updatePost,
  SocialMediaPost,
  SocialPlatform,
  PostStatus,
  PLATFORM_LIMITS,
  validateContent,
  extractHashtags,
} from '../../services/socialMediaService';
import {
  uploadImage,
  validateImageFile,
  formatFileSize,
  deleteImage,
} from '../../services/imageUploadService';
import { Timestamp } from 'firebase/firestore';

interface PostEditorProps {
  postId: string | null; // null for new post, string for editing existing
  onSave: () => void;
  onCancel: () => void;
}

const PLATFORMS: { value: SocialPlatform; label: string; icon: string }[] = [
  { value: 'facebook', label: 'Facebook', icon: '📘' },
  { value: 'instagram', label: 'Instagram', icon: '📷' },
  { value: 'linkedin', label: 'LinkedIn', icon: '💼' },
  { value: 'tiktok', label: 'TikTok', icon: '🎵' },
];

export default function PostEditor({ postId, onSave, onCancel }: PostEditorProps) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  const [content, setContent] = useState('');
  const [platforms, setPlatforms] = useState<SocialPlatform[]>([]);
  const [status, setStatus] = useState<PostStatus>('draft');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [imageUrls, setImageUrls] = useState<string>(''); // Comma-separated URLs
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [customHashtag, setCustomHashtag] = useState('');
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [validation, setValidation] = useState<{ valid: boolean; errors: string[] }>({ valid: true, errors: [] });

  const loadPost = useCallback(async () => {
    if (!postId) {
      setInitialLoading(false);
      return;
    }

    setInitialLoading(true);
    try {
      const post = await getPostById(postId);
      if (post) {
        setContent(post.content);
        setPlatforms(post.platforms);
        setStatus(post.status);
        setImageUrls(post.images.join(', '));
        setUploadedImages(post.images); // Populate uploaded images for editing
        setHashtags(post.hashtags);
        
        if (post.scheduledFor) {
          const date = post.scheduledFor.toDate();
          setScheduledDate(date.toISOString().split('T')[0]);
          setScheduledTime(date.toTimeString().slice(0, 5));
        }
      } else {
        toast({
          title: 'Error',
          description: 'Post not found.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        onCancel();
      }
    } catch (error) {
      console.error('Failed to load post:', error);
      toast({
        title: 'Error',
        description: 'Failed to load post.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setInitialLoading(false);
    }
  }, [postId, onCancel, toast]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);

    try {
      const uploadedUrls: string[] = [];

      for (const file of Array.from(files)) {
        // Validate file
        const validation = validateImageFile(file);
        if (!validation.isValid) {
          toast({
            title: 'Invalid image',
            description: validation.error,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          continue;
        }

        // Upload to Firebase Storage
        const url = await uploadImage(file, 'social-media-images');
        uploadedUrls.push(url);

        toast({
          title: 'Image uploaded!',
          description: `${file.name} uploaded successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      // Add to uploaded images list
      setUploadedImages((prev) => [...prev, ...uploadedUrls]);

      // Also add to imageUrls field (for backward compatibility)
      const currentUrls = imageUrls.split(',').map((u) => u.trim()).filter((u) => u.length > 0);
      const allUrls = [...currentUrls, ...uploadedUrls];
      setImageUrls(allUrls.join(', '));
    } catch (error) {
      console.error('Error uploading images:', error);
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Failed to upload image',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploadingImages(false);
      // Clear file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleRemoveImage = async (url: string) => {
    try {
      // Remove from uploaded images list
      setUploadedImages((prev) => prev.filter((u) => u !== url));

      // Remove from imageUrls field
      const currentUrls = imageUrls.split(',').map((u) => u.trim()).filter((u) => u.length > 0);
      const newUrls = currentUrls.filter((u) => u !== url);
      setImageUrls(newUrls.join(', '));

      // Try to delete from storage (may fail if already deleted, that's okay)
      try {
        await deleteImage(url);
      } catch (deleteError) {
        console.warn('Could not delete image from storage:', deleteError);
      }

      toast({
        title: 'Image removed',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error removing image:', error);
    }
  };

  // Auto-extract hashtags from content
  useEffect(() => {
    const extractedHashtags = extractHashtags(content);
    if (extractedHashtags.length > 0) {
      setHashtags(prev => Array.from(new Set([...prev, ...extractedHashtags])));
    }
  }, [content]);

  // Validate content against platform limits
  useEffect(() => {
    if (platforms.length > 0) {
      const result = validateContent(content, platforms);
      setValidation(result);
    } else {
      setValidation({ valid: true, errors: [] });
    }
  }, [content, platforms]);

  const handleAddHashtag = () => {
    if (customHashtag.trim()) {
      const tag = customHashtag.trim().replace(/^#/, ''); // Remove # if present
      setHashtags(prev => Array.from(new Set([...prev, tag])));
      setCustomHashtag('');
    }
  };

  const handleRemoveHashtag = (tag: string) => {
    setHashtags(prev => prev.filter(t => t !== tag));
  };

  const handleSave = async () => {
    // Validation
    if (!content.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Content is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (platforms.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Select at least one platform',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!validation.valid) {
      toast({
        title: 'Validation Error',
        description: validation.errors.join(', '),
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (status === 'scheduled' && (!scheduledDate || !scheduledTime)) {
      toast({
        title: 'Validation Error',
        description: 'Schedule date and time are required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      const images = imageUrls
        .split(',')
        .map(url => url.trim())
        .filter(url => url.length > 0);

      const scheduledFor = status === 'scheduled' && scheduledDate && scheduledTime
        ? Timestamp.fromDate(new Date(`${scheduledDate}T${scheduledTime}`))
        : null;

      const postData: Omit<SocialMediaPost, 'id' | 'createdAt' | 'updatedAt'> = {
        content: content.trim(),
        platforms,
        images,
        videos: [],
        status,
        scheduledFor,
        publishedAt: status === 'published' ? Timestamp.now() : null,
        createdBy: 'admin@glisten-ecoshine.com', // TODO: Get from auth context
        hashtags,
      };

      if (postId) {
        await updatePost(postId, postData);
        toast({
          title: 'Post updated!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await createPost(postData);
        toast({
          title: 'Post created!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      onSave();
    } catch (error) {
      console.error('Failed to save post:', error);
      toast({
        title: 'Error',
        description: `Failed to save post: ${error}`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Flex justify="center" align="center" height="200px">
        <Spinner size="xl" color="teal.500" />
      </Flex>
    );
  }

  const getCharacterCountColor = () => {
    if (platforms.length === 0) return 'gray';
    const maxAllowed = Math.min(...platforms.map(p => PLATFORM_LIMITS[p].chars));
    const percentage = (content.length / maxAllowed) * 100;
    if (percentage > 100) return 'red';
    if (percentage > 80) return 'orange';
    return 'green';
  };

  const getMinCharLimit = () => {
    if (platforms.length === 0) return 0;
    return Math.min(...platforms.map(p => PLATFORM_LIMITS[p].chars));
  };

  return (
    <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" borderWidth="1px">
      <VStack spacing={6} align="stretch">
        <Heading size="md">{postId ? 'Edit Post' : 'Create New Post'}</Heading>

        {/* Validation Errors */}
        {!validation.valid && (
          <Alert status="error">
            <AlertIcon />
            <Box>
              {validation.errors.map((error, idx) => (
                <Text key={idx}>{error}</Text>
              ))}
            </Box>
          </Alert>
        )}

        {/* Platform Selection */}
        <FormControl isRequired>
          <FormLabel>Select Platforms</FormLabel>
          <CheckboxGroup value={platforms} onChange={(values) => setPlatforms(values as SocialPlatform[])}>
            <Stack direction="row" spacing={4} flexWrap="wrap">
              {PLATFORMS.map(platform => (
                <Checkbox key={platform.value} value={platform.value}>
                  {platform.icon} {platform.label}
                </Checkbox>
              ))}
            </Stack>
          </CheckboxGroup>
          <FormHelperText>
            Select which platforms to post to
          </FormHelperText>
        </FormControl>

        {/* Content Editor */}
        <FormControl isRequired>
          <Flex>
            <FormLabel>Post Content</FormLabel>
            <Spacer />
            <Text fontSize="sm" color={getCharacterCountColor()} fontWeight="bold">
              {content.length} / {getMinCharLimit()} chars
            </Text>
          </Flex>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your post content here... Use #hashtags"
            minH="200px"
            maxLength={3000}
          />
          {platforms.length > 0 && (
            <Progress
              value={(content.length / getMinCharLimit()) * 100}
              colorScheme={validation.valid ? 'green' : 'red'}
              size="sm"
              mt={2}
            />
          )}
          <FormHelperText>
            {platforms.length > 0
              ? `Max ${getMinCharLimit()} characters for selected platforms`
              : 'Select platforms to see character limit'}
          </FormHelperText>
        </FormControl>

        {/* Hashtags */}
        <FormControl>
          <FormLabel>Hashtags</FormLabel>
          <Flex gap={2} mb={2} flexWrap="wrap">
            {hashtags.map(tag => (
              <Badge key={tag} colorScheme="teal" fontSize="md" px={2} py={1} cursor="pointer" onClick={() => handleRemoveHashtag(tag)}>
                #{tag} ✕
              </Badge>
            ))}
          </Flex>
          <HStack>
            <Input
              value={customHashtag}
              onChange={(e) => setCustomHashtag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddHashtag()}
              placeholder="Add custom hashtag"
            />
            <Button onClick={handleAddHashtag}>Add</Button>
          </HStack>
          <FormHelperText>
            Hashtags are auto-detected from content. Click to remove, or add custom ones.
          </FormHelperText>
        </FormControl>

        {/* Image Upload */}
        <FormControl>
          <FormLabel>Images (Optional)</FormLabel>
          
          {/* Upload Button */}
          <VStack align="stretch" spacing={4}>
            <HStack>
              <Button
                as="label"
                htmlFor="image-upload"
                colorScheme="blue"
                isLoading={uploadingImages}
                loadingText="Uploading..."
                cursor="pointer"
              >
                📷 Upload Images
              </Button>
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />
              <Text fontSize="sm" color="gray.600">
                Max 5MB per image. JPG, PNG, GIF, WebP
              </Text>
            </HStack>

            {/* Image Previews */}
            {uploadedImages.length > 0 && (
              <VStack align="stretch" spacing={2}>
                <Text fontWeight="medium">Uploaded Images:</Text>
                {uploadedImages.map((url, index) => (
                  <Flex
                    key={url}
                    p={3}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    align="center"
                    gap={3}
                  >
                    <Box
                      w="80px"
                      h="80px"
                      borderRadius="md"
                      overflow="hidden"
                      flexShrink={0}
                      bg="gray.100"
                    >
                      <img
                        src={url}
                        alt={`Upload ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </Box>
                    <Box flex={1} overflow="hidden">
                      <Text fontSize="sm" isTruncated>
                        {url.split('/').pop()?.split('?')[0]}
                      </Text>
                      <Text fontSize="xs" color="gray.500" isTruncated>
                        {url}
                      </Text>
                    </Box>
                    <Button
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => handleRemoveImage(url)}
                    >
                      Remove
                    </Button>
                  </Flex>
                ))}
              </VStack>
            )}

            {/* Manual URL Input (backup) */}
            <Box>
              <FormLabel fontSize="sm" mb={1}>Or enter image URLs manually:</FormLabel>
              <Input
                value={imageUrls}
                onChange={(e) => setImageUrls(e.target.value)}
                placeholder="https://example.com/image.jpg, https://example.com/image2.jpg"
                size="sm"
              />
              <FormHelperText fontSize="xs">
                Comma-separated URLs. Uploaded images are automatically added here.
              </FormHelperText>
            </Box>
          </VStack>

          {platforms.includes('instagram') && uploadedImages.length === 0 && (
            <Alert status="warning" mt={2}>
              <AlertIcon />
              Instagram requires at least one image!
            </Alert>
          )}
        </FormControl>

        {/* Status & Scheduling */}
        <FormControl isRequired>
          <FormLabel>Post Status</FormLabel>
          <Select value={status} onChange={(e) => setStatus(e.target.value as PostStatus)}>
            <option value="draft">📝 Save as Draft</option>
            <option value="scheduled">📅 Schedule for Later</option>
            <option value="published">✅ Publish Now</option>
          </Select>
        </FormControl>

        {status === 'scheduled' && (
          <HStack>
            <FormControl isRequired>
              <FormLabel>Schedule Date</FormLabel>
              <Input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Schedule Time</FormLabel>
              <Input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
              />
            </FormControl>
          </HStack>
        )}

        {/* Action Buttons */}
        <HStack spacing={4} mt={6}>
          <Button
            colorScheme="teal"
            onClick={handleSave}
            isLoading={loading}
            isDisabled={!validation.valid}
            size="lg"
          >
            {postId ? 'Update Post' : 'Create Post'}
          </Button>
          <Button variant="outline" onClick={onCancel} size="lg">
            Cancel
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
